import React, {useEffect} from "react";
import {useState} from "react";
import {QueryClientProvider, QueryClient} from "react-query";
import {BrowserRouter, Routes, Route} from "react-router-dom";
import PageLoading from "./components/layouts/loading";
import {AppContext} from "./contexts/app-context";
import "./styles/tailwind.output.css";
import {AppGlobalData} from "./types/AppGlobalData";

// components import
const AgencyClientsPage = React.lazy(
  () => import("./pages/protected/agency/clients/ClientsPage")
);
const AgencyDashboardPage = React.lazy(
  () => import("./pages/protected/agency/DashboardPage")
);
const AgencyEmployeesPage = React.lazy(
  () => import("./pages/protected/agency/employees/EmployeesPage")
);
const AccessDeniedPage = React.lazy(
  () => import("./pages/public/AccessDeniedPage")
);
const AgencySignupPage = React.lazy(
  () => import("./pages/public/AgencySignupPage")
);
const LandingPage = React.lazy(() => import("./pages/public/LandingPage"));
const LoginPage = React.lazy(
  () => import("./pages/public/account/login/LoginPage")
);
const RegisterPage = React.lazy(
  () => import("./pages/public/account/register/RegisterPage")
);
const ActivateAccountPage = React.lazy(
  () => import("./pages/public/account/activate/ActivateAccountPage")
);
const EmployeeOnboardingPage = React.lazy(
  () => import("./pages/public/EmployeeOnboardingPage")
);
const EmployeeDashboardPage = React.lazy(
  () => import("./pages/protected/employee/DashboardPage")
);
const AgencyBroadcastsPage = React.lazy(
  () => import("./pages/protected/agency/BroadcastPage")
);
const ServiceUsersPage = React.lazy(
  () => import("./pages/protected/agency/serviceusers/ServiceUsersPage")
);
const CarePlansPage = React.lazy(
  () => import("./pages/protected/agency/clients/CarePlansPage")
);
const RiskAssessmentsPage = React.lazy(
  () => import("./pages/protected/agency/clients/RiskAssessmentsPage")
);

const ShiftsPage = React.lazy(
  () => import("./pages/protected/agency/shifts/ShiftsPage")
);
const ServiceUserPage = React.lazy(
  () => import("./pages/protected/agency/clients/ServiceUsersPage")
);
const DocumentTemplatePage = React.lazy(
  () => import("./pages/protected/agency/DocumentTemplatePage")
);
const PdfDocumentPreviewPage = React.lazy(
  () => import("./pages/protected/agency/PdfDocumentPreviewPage")
);
const UpdateDocumentTemplatePage = React.lazy(
  () => import("./pages/protected/agency/UpdateDocumentTemplatePage")
);

const ServiceUserAddressesPage = React.lazy(
  () => import("./pages/protected/agency/serviceusers/ServiceUserAddressesPage")
);

const ServiceUserOtherDetailsPage = React.lazy(
  () =>
    import("./pages/protected/agency/serviceusers/ServiceUserOtherDetailsPage")
);

const ServiceUserRelativesPage = React.lazy(
  () => import("./pages/protected/agency/serviceusers/ServiceUserRelativesPage")
);

const CqcMedicationPage = React.lazy(
  () => import("./pages/protected/agency/serviceusers/mar/CqcMedicationPage")
);
const MarChartPage = React.lazy(
  () => import("./pages/protected/agency/serviceusers/mar/MarChartPage")
);
const MedicationRoundPage = React.lazy(
  () => import("./pages/protected/agency/serviceusers/mar/MedicationRoundPage")
);
const PrnTrackerPage = React.lazy(
  () => import("./pages/protected/agency/serviceusers/mar/PrnTrackerPage")
);
const CqcInspectionReportPage = React.lazy(
  () => import("./pages/protected/agency/serviceusers/mar/CqcInspectionReportPage")
);

const ServiceUserMedicinesPage = React.lazy(
  () => import("./pages/protected/agency/serviceusers/ServiceUserMedicinesPage")
);

const SuperAdminDashboardPage = React.lazy(
  () => import("./pages/protected/super-admin/SuperAdminDashboardPage")
);

const BusinessMaintenancePage = React.lazy(
  () => import("./pages/protected/super-admin/BusinessMaintenancePage")
);

const SuperAdminComingSoonPage = React.lazy(
  () => import("./pages/protected/super-admin/ComingSoonPage")
);

const HrDocumentDashboardPage = React.lazy(
  () => import("./pages/protected/admin/hr/documents/HrDocumentDashboardPage")
);
const HrEmployeeCompliancePage = React.lazy(
  () => import("./pages/protected/admin/hr/documents/HrEmployeeCompliancePage")
);
const HrDocumentConfigPage = React.lazy(
  () => import("./pages/protected/admin/hr/documents/HrDocumentConfigPage")
);

const DemographicGovernancePage = React.lazy(
  () => import("./pages/protected/agency/serviceusers/DemographicGovernancePage")
);

const EscalationPlanPage = React.lazy(
  () => import("./pages/protected/agency/serviceusers/EscalationPlanPage")
);

const AssessmentBundlePage = React.lazy(
  () => import("./pages/protected/agency/serviceusers/AssessmentBundlePage")
);

const CarePlanListPage = React.lazy(
  () => import("./pages/protected/agency/careplan/CarePlanListPage")
);

const RiskAssessmentPage = React.lazy(
  () => import("./pages/protected/agency/serviceusers/RiskAssessmentPage")
);

const CarePlanPage = React.lazy(
  () => import("./pages/protected/agency/serviceusers/CarePlanPage")
);

const UserClientMappingPage = React.lazy(
  () => import("./pages/protected/admin/users/UserClientMappingPage")
);

const AgencyReportsPage = React.lazy(
  () => import("./pages/protected/agency/reports/Index")
);

const AgencyHandoverReportPage = React.lazy(
  () => import("./pages/protected/agency/reports/HandoverPage")
);

const TermsPage = React.lazy(() => import("./pages/public/terms/TermsPage"));
const TestPage = React.lazy(() => import("./pages/public/TestPage"));
const UserListPage = React.lazy(
  () => import("./pages/protected/admin/users/UserListPage")
);
const CustomerListPage = React.lazy(
  () => import("./pages/protected/agency/customers/CustomerListPage")
);
const ClientShiftsPage = React.lazy(
  () => import("./pages/protected/agency/clients/ClientShiftsPage")
);

const AgencyRotaPage = React.lazy(
  () => import("./pages/protected/agency/AgencyRotaPage")
);

const BroadcastDashboardPage = React.lazy(
  () => import("./pages/protected/agency/clients/BroadcastDashboardPage")
);

const AuditPage = React.lazy(
  () => import("./pages/protected/agency/reports/AuditPage")
);

const AuditByUserPage = React.lazy(
  () => import("./pages/protected/agency/reports/AuditByUserPage")
);

const AgencyHomePage = React.lazy(
  () => import("./pages/protected/agency/clients/AgencyHomePage")
);

const ShiftAcceptancePage = React.lazy(
  () => import("./pages/public/ShiftAcceptancePage")
);

const EmployeeShiftsPage = React.lazy(
  () => import("./pages/protected/employee/ShiftsPage")
);

const ShiftManagePage = React.lazy(
  () => import("./pages/protected/agency/rota/ShiftManagePage")
);
const MedicationReportPage = React.lazy(
  () => import("./pages/protected/agency/reports/MedicationReportPage")
);
const HolidayManagementPage = React.lazy(
  () => import("./pages/protected/admin/hr/holiday/HolidayManagementPage")
);
const HolidayRequestPage = React.lazy(
  () => import("./pages/protected/admin/hr/holiday/HolidayRequestPage")
);
const EmployeeHolidaysPage = React.lazy(
  () => import("./pages/protected/admin/hr/holiday/EmployeeHolidaysPage")
);
const HolidayAllocationPage = React.lazy(
  () => import("./pages/protected/admin/hr/holiday/HolidayAllocationPage")
);
const EmployeeManagementPage = React.lazy(
  () => import("./pages/protected/admin/hr/employee/employeeManagementPage")
);
const HolidayApprovalPage = React.lazy(
  () => import("./pages/protected/admin/hr/holiday/HolidayApprovalPage")
);
const HolidayTypesPage = React.lazy(
  () => import("./pages/protected/admin/hr/holiday/HolidayTypesPage")
);
const EmployeeHolidayRequestPage = React.lazy(
  () => import("./pages/protected/employee/HolidayRequestPage")
);

const DepartmentPage = React.lazy(
  () => import("./pages/protected/admin/hr/DepartmentPage")
);
const GlobalHolidayPage = React.lazy(
  () => import("./pages/protected/admin/hr/holiday/GlobalHolidayPage")
);
const HolidayRequestsApprovalPage = React.lazy(
  () => import("./pages/protected/admin/hr/holiday/HolidayRequestsApprovalPage")
);
const EmployeeAddressPage = React.lazy(
  () => import("./pages/protected/admin/hr/employee/employeeAddressPage")
);
const HolidayRequestsPage = React.lazy(
  () => import("./pages/protected/admin/hr/holiday/HolidayRequestsPage")
);
const HolidayRequestAdminPage = React.lazy(
  () => import("./pages/protected/admin/hr/holiday/HolidayRequestsAdminPage")
)
const queryClient = new QueryClient();

function App() {
  const [appData, setGlobalData] = useState<AppGlobalData>(() => {
    const savedGlobalData = sessionStorage.getItem("appData");
    return savedGlobalData ? JSON.parse(savedGlobalData) : {};
  });
  const appDataValue = {appData, setGlobalData};

  useEffect(() => {
    if (appData) {
      sessionStorage.setItem("appData", JSON.stringify(appData));
    } else {
      sessionStorage.removeItem("appData");
    }
  }, [appData]);

  return (
    <QueryClientProvider client={queryClient}>
      <AppContext.Provider value={appDataValue}>
        <BrowserRouter>
          <Routes>
            <Route
              path="/"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <LoginPage />
                </React.Suspense>
              }
            />
            <Route
              path="/agency-signup"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <AgencySignupPage />
                </React.Suspense>
              }
            />
            <Route
              path="/agency/reports"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <AgencyReportsPage />
                </React.Suspense>
              }
            />
            <Route
              path="/agency/reports/hanover/:clientId/client"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <AgencyHandoverReportPage />
                </React.Suspense>
              }
            />
            <Route
              path="/agency/reports/Handover"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <AgencyHandoverReportPage />
                </React.Suspense>
              }
            />
            <Route
              path="/login"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <LoginPage />
                </React.Suspense>
              }
            />
            <Route
              path="/terms"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <TermsPage />
                </React.Suspense>
              }
            />
            <Route
              path="/register/:businessId/:email"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <RegisterPage />
                </React.Suspense>
              }
            />
            <Route
              path="/activate/:email"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <ActivateAccountPage />
                </React.Suspense>
              }
            />
            <Route
              path="/admin/user-client-mapping"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <UserClientMappingPage />
                </React.Suspense>
              }
            />

            <Route
              path="/test"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <TestPage />
                </React.Suspense>
              }
            />

            {/* {userValue.user.userId && (
              <Route path="/agency/dashboard" element={<AgencyDashboard />} />
            )} */}
            <Route
              path="/agency/dashboard"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <AgencyDashboardPage />
                </React.Suspense>
              }
            />
            <Route
              path="/employee/dashboard"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <EmployeeDashboardPage />
                </React.Suspense>
              }
            />
            <Route
              path="/agency/clients"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <AgencyClientsPage key="1233" />
                </React.Suspense>
              }
            />
            <Route
              path="/agency/document-template/agency/:agencyId/client/:clientId"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <DocumentTemplatePage />
                </React.Suspense>
              }
            />
            <Route
              path="/agency/document-template-edit/agency/:agencyId/client/:clientId"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <UpdateDocumentTemplatePage />
                </React.Suspense>
              }
            />
            <Route
              path="/agency/pdf-document-preview/agency/:agencyId/client/:clientId"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <PdfDocumentPreviewPage />
                </React.Suspense>
              }
            />
            <Route
              path="/agency/:agencyId/client/:clientId/service-users"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <ServiceUserPage />
                </React.Suspense>
              }
            />
            <Route
              path="/agency/employees"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <AgencyEmployeesPage />
                </React.Suspense>
              }
            />
            <Route
              path="/agency/broadcasts"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <AgencyBroadcastsPage />
                </React.Suspense>
              }
            />
            {/* <Route
              path="/agency/employee/:employeeId/addresses"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <EmployeeAddressPage />
                </React.Suspense>
              }
            />
            <Route
              path="/agency/employee/:employeeId/documents"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <EmployeeDocumentsPage />
                </React.Suspense>
              }
            /> */}

            <Route
              path="/agency/:agencyId/client/:clientId/care-plans"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <CarePlansPage />
                </React.Suspense>
              }
            />

            <Route
              path="/agency/:agencyId/client/:clientId/risk-assessments"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <RiskAssessmentsPage />
                </React.Suspense>
              }
            />
            <Route
              path="/agency/shifts"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <ShiftsPage />
                </React.Suspense>
              }
            />

            <Route
              path="/agency/service-users/:clientId/client"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <ServiceUsersPage />
                </React.Suspense>
              }
            />

            <Route
              path="/service-user/:serviceUserId/addresses"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <ServiceUserAddressesPage />
                </React.Suspense>
              }
            />

            <Route
              path="/service-user/:serviceUserId/other-details"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <ServiceUserOtherDetailsPage />
                </React.Suspense>
              }
            />

            <Route
              path="/service-user/:serviceUserId/relatives"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <ServiceUserRelativesPage />
                </React.Suspense>
              }
            />

            <Route
              path="/service-user/:serviceUserId/medications"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <ServiceUserMedicinesPage />
                </React.Suspense>
              }
            />
            <Route
              path="/service-user/:serviceUserId/mar"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <CqcMedicationPage />
                </React.Suspense>
              }
            />
            <Route
              path="/service-user/:serviceUserId/mar/chart"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <MarChartPage />
                </React.Suspense>
              }
            />
            <Route
              path="/service-user/:serviceUserId/mar/round"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <MedicationRoundPage />
                </React.Suspense>
              }
            />
            <Route
              path="/service-user/:serviceUserId/mar/prn"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <PrnTrackerPage />
                </React.Suspense>
              }
            />
            <Route
              path="/service-user/:serviceUserId/mar/report"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <CqcInspectionReportPage />
                </React.Suspense>
              }
            />

            {/* ── Super Admin (super-sys-admin role only) ──────────────── */}
            <Route
              path="/super-admin/dashboard"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <SuperAdminDashboardPage />
                </React.Suspense>
              }
            />

            <Route
              path="/super-admin/businesses"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <BusinessMaintenancePage />
                </React.Suspense>
              }
            />

            <Route
              path="/super-admin/licensing"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <SuperAdminComingSoonPage
                    title="Licensing"
                    description="Subscription tiers, seat counts, and renewal dates — coming soon."
                  />
                </React.Suspense>
              }
            />

            <Route
              path="/super-admin/reports"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <SuperAdminComingSoonPage
                    title="Reports"
                    description="Cross-agency usage, billing, and activity reports — coming soon."
                  />
                </React.Suspense>
              }
            />

            <Route
              path="/super-admin/audits"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <SuperAdminComingSoonPage
                    title="Audits"
                    description="Platform-wide audit log across all agencies and modules — coming soon."
                  />
                </React.Suspense>
              }
            />

            <Route
              path="/super-admin/users"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <SuperAdminComingSoonPage
                    title="User Management"
                    description="View and manage user accounts across every business — coming soon."
                  />
                </React.Suspense>
              }
            />

            <Route
              path="/super-admin/settings"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <SuperAdminComingSoonPage
                    title="Platform Settings"
                    description="Global configuration, feature flags, and system defaults — coming soon."
                  />
                </React.Suspense>
              }
            />

            <Route
              path="/care-plan/active"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <CarePlanListPage />
                </React.Suspense>
              }
            />

            <Route
              path="/care-plan/due-reviews"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <CarePlanListPage />
                </React.Suspense>
              }
            />

            <Route
              path="/care-plan/high-risk"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <CarePlanListPage />
                </React.Suspense>
              }
            />

            <Route
              path="/care-plan/risk-assessment/new"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <CarePlanListPage />
                </React.Suspense>
              }
            />

            <Route
              path="/service-user/:serviceUserId/assessment-bundle"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <AssessmentBundlePage />
                </React.Suspense>
              }
            />

            <Route
              path="/service-user/:serviceUserId/assessment-bundle/demographics"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <DemographicGovernancePage />
                </React.Suspense>
              }
            />

            <Route
              path="/service-user/:serviceUserId/assessment-bundle/escalation"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <EscalationPlanPage />
                </React.Suspense>
              }
            />

            <Route
              path="/service-user/:serviceUserId/risk-assessment"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <RiskAssessmentPage />
                </React.Suspense>
              }
            />

            <Route
              path="/service-user/:serviceUserId/care-plan"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <CarePlanPage />
                </React.Suspense>
              }
            />

            <Route
              path="/agency/users"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <UserListPage />
                </React.Suspense>
              }
            />

            <Route
              path="/agency/customers"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <CustomerListPage />
                </React.Suspense>
              }
            />

            <Route
              path="/agency/client/:clientId/shifts"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <ClientShiftsPage />
                </React.Suspense>
              }
            />
            <Route
              path="/access-denied"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <AccessDeniedPage />
                </React.Suspense>
              }
            />
            <Route
              path="/agency/reports/audit"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <AuditPage />
                </React.Suspense>
              }
            />
            <Route
              path="/agency/reports/auditbyuser"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <AuditByUserPage />
                </React.Suspense>
              }
            />
            <Route
              path="/agency/client/:clientId/broadcastdashboard"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <BroadcastDashboardPage />
                </React.Suspense>
              }
            />
            <Route
              path="/agency/rota"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <AgencyRotaPage />
                </React.Suspense>
              }
            />
            <Route
              path="/agency/clients/agencyhomepage"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <AgencyHomePage />
                </React.Suspense>
              }
            />
            <Route
              path="/agency/rota/shifts"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <ShiftManagePage />
                </React.Suspense>
              }
            />
            <Route
              path="/shift-acceptance/:broadcastId/:employeeId"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <ShiftAcceptancePage />
                </React.Suspense>
              }
            />

            <Route
              path="/employee/shifts"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <EmployeeShiftsPage />
                </React.Suspense>
              }
            />
            <Route
              path="/agency/medication-report"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <MedicationReportPage />
                </React.Suspense>
              }
            />
            <Route
              path="/admin/hr/holiday/holiday-management"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <HolidayManagementPage />
                </React.Suspense>
              }
            />
            <Route
              path="/employee/my-holiday-requests"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <EmployeeHolidaysPage />
                </React.Suspense>
              }
            />
            <Route
              path="/employee/new-holiday-request"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <HolidayRequestPage />
                </React.Suspense>
              }
            />
            <Route
              path="/admin/hr/holiday/holiday-allocation"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <HolidayAllocationPage />
                </React.Suspense>
              }
            />
            <Route
              path="/admin/hr/employees"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <EmployeeManagementPage />
                </React.Suspense>
              }
            />
            <Route
              path="/admin/hr/employees/:employeeId/address"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <EmployeeAddressPage />
                </React.Suspense>
              }
            />

            <Route
              path="/admin/hr/holiday/holiday-approval"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <HolidayApprovalPage />
                </React.Suspense>
              }
            />
            <Route
              path="/admin/hr/holiday/holiday-types"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <HolidayTypesPage />
                </React.Suspense>
              }
            />
            <Route
              path="/admin/hr/documents"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <HrDocumentDashboardPage />
                </React.Suspense>
              }
            />
            <Route
              path="/admin/hr/documents/config"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <HrDocumentConfigPage />
                </React.Suspense>
              }
            />
            <Route
              path="/admin/hr/documents/employee/:employeeId"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <HrEmployeeCompliancePage />
                </React.Suspense>
              }
            />
            <Route
              path="/admin/hr/departments"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <DepartmentPage />
                </React.Suspense>
              }
            />
            <Route
              path="/admin/hr/holiday/global-holiday-setup"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <GlobalHolidayPage />
                </React.Suspense>
              }
            />
            <Route
              path="/employee/holiday-request"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <EmployeeHolidayRequestPage />
                </React.Suspense>
              }
            />
            <Route
              path="/admin/hr/holiday/approval"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <HolidayRequestsApprovalPage />
                </React.Suspense>
              }
            />
            <Route
              path="/admin/hr/holiday/requests"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <HolidayRequestsPage />
                </React.Suspense>
              }
            />
             <Route
              path="/admin/hr/holiday/admin-holiday-requests"
              element={
                <React.Suspense fallback={<PageLoading />}>
                  <HolidayRequestAdminPage />
                </React.Suspense>
              }
            />
          </Routes>
        </BrowserRouter>
      </AppContext.Provider>
    </QueryClientProvider>
  );
}

export default App;
